import React, { MouseEventHandler, useEffect } from 'react';
import useAuth from '#/hooks/useAuth';
import useDeviceId from '#/hooks/useDeviceId';
import {
  createActionPlayerSetCurrentIsFavorite,
  createActionPlayerSetCurrentTrack,
  createActionPlayerSetIsActive,
  createActionPlayerSetIsPaused,
  createActionPlayerSetPlayer,
  createActionPlayerSetPosition,
  createActionPlayerSetRepeat,
  createActionPlayerSetShuffle,
  createActionPlayerSetVolume,
} from '#/redux/actions/player';
import { TRootState } from '#/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import IconButton from '#/components/Form/IconButton/IconButton';
import { Link } from 'react-router-dom';
import { ROUTE_NAMES } from '#/router/routes';
import ApiManager from '#/services/api/ApiManager';
import { spotifyApiConfig, spotifyWebPlaybackId } from '#/utils/constants';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import msToReadableTime from '#/utils/msToReadableTime';
import Slider from '#/components/Form/Slider/Slider';
import scriptIsLoaded from '#/utils/scriptIsLoaded';
import { urisParameter } from '#/utils/spotifyApiHelpers';

const playerSelector = (state: TRootState) => state.player;

const PlayerIconButton = ({
  icon,
  onClick,
}: {
  icon: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) => {
  return (
    <IconButton
      icon={icon}
      iconClassName="text-3xl text-theme-light-gray hover:text-theme-white"
      className="p-0"
      onClick={onClick}
    />
  );
};

const Player = () => {
  const { retrieveDeviceId, storeDeviceId, removeDeviceId } = useDeviceId();

  const playerSlice = useSelector(playerSelector);

  const {
    player,
    isActive,
    isPaused,
    currentTrack,
    position,
    shuffle,
    repeat,
    currentIsFavorite,
    volume,
  } = playerSlice;

  const dispatch = useDispatch();

  const { retrieveAuth } = useAuth();

  const protectedSpotify = useProtectedSpotify();

  useEffect(() => {
    if (player === undefined) {
      const script = document.createElement('script');
      script.id = spotifyWebPlaybackId;
      script.src = 'https://sdk.scdn.co/spotify-player.js';
      script.async = true;
      const auth = retrieveAuth();
      if (!scriptIsLoaded(script.src) && auth) {
        document.body.append(script);

        window.onSpotifyWebPlaybackSDKReady = () => {
          const initialVolume = 0.5;
          const newPlayer = new window.Spotify.Player({
            name: 'Web Playback SDK',
            getOAuthToken: (callback) => {
              callback(auth.token);
            },
            volume: initialVolume,
          });

          dispatch(createActionPlayerSetPlayer(newPlayer));

          newPlayer.on('playback_error', (error) => {
            // eslint-disable-next-line no-console
            console.log('error playback', error);
          });

          newPlayer.addListener('ready', ({ device_id }) => {
            storeDeviceId(device_id);
          });

          newPlayer.addListener('not_ready', () => {
            removeDeviceId();
          });

          newPlayer.addListener('player_state_changed', (state) => {
            if (!state) {
              return;
            }

            // eslint-disable-next-line no-console
            console.log('player state chaged', state);

            dispatch(createActionPlayerSetVolume(initialVolume * 100));

            dispatch(
              createActionPlayerSetCurrentTrack(
                state.track_window.current_track
              )
            );

            dispatch(createActionPlayerSetIsPaused(state.paused));

            dispatch(createActionPlayerSetPosition(state.position));

            dispatch(createActionPlayerSetRepeat(state.repeat_mode === 1));

            dispatch(createActionPlayerSetShuffle(state.shuffle));

            newPlayer.getCurrentState().then((state) => {
              dispatch(createActionPlayerSetIsActive(state ? true : false));
            });
          });

          newPlayer.connect();
        };
      }
    }
  }, [dispatch, player, removeDeviceId, retrieveAuth, storeDeviceId]);

  useEffect(() => {
    return () => {
      const auth = retrieveAuth();

      if (!auth) {
        const elementScript = document.querySelector(
          `#${spotifyWebPlaybackId}`
        );

        if (elementScript) {
          elementScript.remove();
        }

        const elementIframe = document.querySelector('iframe');

        if (elementIframe) {
          elementIframe.remove();
        }
      }
    };
  }, [retrieveAuth]);

  useEffect(() => {
    let interval: NodeJS.Timer;

    if (isActive && !isPaused) {
      interval = setInterval(
        () => dispatch(createActionPlayerSetPosition(position + 1000)),
        1000
      );
    }

    return () => {
      clearInterval(interval);
    };
  }, [dispatch, isPaused, position, currentTrack, isActive]);

  useEffect(() => {
    const getIsFavorite = async () => {
      if (currentTrack && currentTrack.id) {
        const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);
        const responseIsFavorite = await apiManager.getAllEntities<boolean[]>(
          'libraryContains',
          {
            uris: urisParameter('track', currentTrack.id),
          }
        );

        if (!('error' in responseIsFavorite)) {
          dispatch(
            createActionPlayerSetCurrentIsFavorite(responseIsFavorite.data[0])
          );
        }
      }
    };

    getIsFavorite();
  }, [currentTrack, dispatch, protectedSpotify]);

  const handleClickFavorite = async () => {
    if (currentTrack) {
      const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);
      if (!currentTrack.id) {
        return;
      }
      if (currentIsFavorite) {
        const responseRemoveFromFavorites = await apiMannager.deleteEntity(
          'library',
          '',
          {
            uris: urisParameter('track', currentTrack.id),
          }
        );

        if ('error' in responseRemoveFromFavorites) {
          //TODO: handle error
        } else {
          dispatch(createActionPlayerSetCurrentIsFavorite(false));
        }
      } else {
        const responseAddToFavorites = await apiMannager.replaceEntity(
          'library',
          '',
          undefined,
          {
            uris: urisParameter('track', currentTrack.id),
          }
        );

        if ('error' in responseAddToFavorites) {
          //TODO: handle error
        } else {
          //
          dispatch(createActionPlayerSetCurrentIsFavorite(true));
        }
      }
    }
  };

  const handlePlayResumeOnClick = () => {
    player?.togglePlay().catch(() => {
      // eslint-disable-next-line no-console
      console.log('Cant play/resume');
    });
  };

  const handlePreviousOnClick = () => {
    player?.previousTrack().then(() => {
      // eslint-disable-next-line no-console
      console.log('Cant skip to previous');
    });
  };

  const handleNextOnClick = () => {
    player?.nextTrack().then(() => {
      // eslint-disable-next-line no-console
      console.log('Cant skip to next');
    });
  };

  const handleChangePosition = (position: number) => {
    player?.seek(position).then(() => {
      // eslint-disable-next-line no-console
      console.log('Cant seek to position');
    });
  };

  const handleShuffleOnClick = async () => {
    const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

    const response = await apiManager.replaceEntity(
      'playerShuffle',
      '',
      undefined,
      {
        state: !shuffle,
        device_id: retrieveDeviceId(),
      }
    );
    if ('error' in response) {
      // TODO: handle error
      // eslint-disable-next-line no-console
      console.log('Cant activate shuffle');
    }
  };

  const handleRepeatOnClick = async () => {
    const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

    const response = await apiManager.replaceEntity(
      'playerRepeat',
      '',
      undefined,
      {
        state: repeat ? 'off' : 'context',
        device_id: retrieveDeviceId(),
      }
    );
    if ('error' in response) {
      // TODO: handle error
      // eslint-disable-next-line no-console
      console.log('Cant activatte repeat');
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    player
      ?.setVolume(newVolume / 100)
      .then(() => {
        dispatch(createActionPlayerSetVolume(newVolume));
      })
      .catch(() => {
        // TODO: handle error
        // eslint-disable-next-line no-console
        console.log('Cant change volume');
      });
  };

  const handleMuteChange = () => {
    if (volume > 0) {
      handleVolumeChange(0);
    } else {
      handleVolumeChange(50);
    }
  };

  return (
    <div className="bg-black text-white h-theme-player-height flex select-none">
      <div className="flex-[5] flex gap-4 p-4 pt-2 items-center">
        {isActive && currentTrack && (
          <>
            <Link
              to={`${ROUTE_NAMES.ALBUM}/${
                currentTrack.album.uri.split(':')[2]
              }`}
              className=" h-full aspect-square rounded-md overflow-hidden"
            >
              <img
                className="object-cover w-full h-full"
                src={currentTrack.album.images[0].url}
                alt=""
                loading="lazy"
              />
            </Link>
            <div className="flex flex-col justify-center">
              <Link
                to={`${ROUTE_NAMES.TRACK}/${currentTrack.id}`}
                className="text-sm font-bold hover:underline line-clamp-1"
              >
                {currentTrack.name}
              </Link>
              <Link
                to={`${ROUTE_NAMES.ARTIST}/${
                  currentTrack.artists[0].uri?.split(':')[2]
                }`}
                className="text-xs text-theme-light-gray hover:text-theme-white hover:underline line-clamp-1"
              >
                {currentTrack.artists[0].name}
              </Link>
            </div>
            <div className="flex justify-center items-center">
              <span
                onClick={handleClickFavorite}
                className={`material-icons text-lg cursor-default ${
                  currentIsFavorite
                    ? 'text-theme-green'
                    : 'text-theme-light-gray hover:text-white '
                }  `}
              >
                {currentIsFavorite ? 'favorite' : 'favorite_border'}{' '}
              </span>
            </div>
          </>
        )}
      </div>
      <div className="flex-[6] flex flex-col">
        <div className="flex gap-2 justify-center items-center">
          <IconButton
            icon="shuffle"
            className="p-0"
            iconClassName={` text-2xl rounded-full ${
              shuffle
                ? 'text-theme-green hover:text-theme-light-green'
                : 'text-theme-light-gray hover:text-theme-white'
            }`}
            onClick={handleShuffleOnClick}
          />
          <PlayerIconButton
            icon="skip_previous"
            onClick={handlePreviousOnClick}
          />
          <IconButton
            icon={
              isPaused || !currentTrack
                ? 'play_circle_filled'
                : 'pause_circle_filled'
            }
            className="p-0"
            iconClassName=" text-[2.5rem] rounded-full hover:outline -outline-offset-4 outline-2"
            onClick={handlePlayResumeOnClick}
          />
          <PlayerIconButton icon="skip_next" onClick={handleNextOnClick} />
          <IconButton
            icon="repeat"
            className="p-0"
            iconClassName={` text-2xl rounded-full ${
              repeat
                ? 'text-theme-green hover:text-theme-light-green'
                : 'text-theme-light-gray hover:text-theme-white'
            }`}
            onClick={handleRepeatOnClick}
          />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-theme-light-gray">
            {msToReadableTime(position)}
          </span>
          <div className="flex-1">
            <Slider
              min={0}
              max={currentTrack ? currentTrack.duration_ms : 0}
              value={position}
              progress
              thumbVisible="hover"
              onChange={handleChangePosition}
            />
          </div>
          <span className="text-xs text-theme-light-gray">
            {msToReadableTime(currentTrack ? currentTrack.duration_ms : 0)}
          </span>
        </div>
      </div>
      <div className="flex-[5] gap-2 flex justify-end items-center p-4 pt-2">
        <span
          onClick={handleMuteChange}
          className="material-icons text-theme-light-gray cursor-default"
        >
          {volume === 0
            ? 'volume_off'
            : volume <= 33
            ? 'volume_mute'
            : volume <= 66
            ? 'volume_down'
            : 'volume_up'}
        </span>
        <div className=" w-[94px]">
          <Slider
            value={volume}
            min={0}
            max={100}
            onChange={handleVolumeChange}
            progress
            thumbVisible="hover"
          />
        </div>
      </div>
    </div>
  );
};

export default Player;
